import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { MapPin, Banknote, Calendar, Loader, ArrowRight } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import DriverLayout from '../../components/driver/DriverLayout';

const DriverHistory = () => {
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRideHistory();
  }, []);

  const fetchRideHistory = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/drivers/my-rides');
      setRides(response.data.rides || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ride history:', error);
      toast({
        title: 'Error',
        description: 'Failed to load ride history',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      accepted: 'Accepted',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <DriverLayout>
        <div className="flex items-center justify-center h-96">
          <Loader className="h-12 w-12 animate-spin text-pink-600" />
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Rides</h1>
          <p className="text-gray-600 mt-1">Your complete ride history</p>
        </div>

        {rides.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <MapPin className="h-8 w-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Rides Yet</h2>
            <p className="text-gray-600">
              Your completed rides will appear here
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {rides.map((ride) => (
              <Card key={ride.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-3">
                    {/* Status and Date */}
                    <div className="flex items-center gap-3">
                      <Badge className={getStatusColor(ride.status)}>
                        {getStatusLabel(ride.status)}
                      </Badge>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        {new Date(ride.createdAt).toLocaleDateString()} at{' '}
                        {new Date(ride.createdAt).toLocaleTimeString()}
                      </div>
                    </div>

                    {/* Rider Info */}
                    {ride.userName && (
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {ride.userName.charAt(0)}
                        </div>
                        <span className="text-gray-900 font-medium">{ride.userName}</span>
                      </div>
                    )}

                    {/* Route */}
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-600">Pickup</p>
                          <p className="text-sm font-medium text-gray-900">{ride.pickup}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-600">Dropoff</p>
                          <p className="text-sm font-medium text-gray-900">{ride.dropoff}</p>
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="flex items-center gap-6 text-sm pt-2">
                      <div className="flex items-center gap-1">
                        <Banknote className="h-4 w-4 text-green-600" />
                        <span className="font-semibold text-green-600">K{ride.price}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-gray-600" />
                        <span className="text-gray-600">{ride.distance?.toFixed(1) || 'N/A'} mi</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Button or Commission Info */}
                  <div className="flex flex-col items-end gap-2">
                    {/* Show "Open Ride" button for active rides */}
                    {(ride.status === 'accepted' || ride.status === 'in_progress') && (
                      <Button
                        onClick={() => navigate('/driver/active-ride', { state: { ride } })}
                        className="bg-pink-600 hover:bg-pink-700"
                      >
                        Open Ride
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                    
                    {/* Commission Info for completed rides */}
                    {ride.status === 'completed' && (
                      <div className="text-right">
                        <p className="text-xs text-gray-600">Commission Paid</p>
                        <p className="text-lg font-semibold text-red-600">-K{(ride.price * 0.20)?.toFixed(2) || '0.00'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Summary */}
        {rides.length > 0 && (
          <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50">
            <h3 className="font-semibold text-gray-900 mb-4">Summary</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Total Rides</p>
                <p className="text-2xl font-bold text-gray-900">{rides.length}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">
                  {rides.filter(r => r.status === 'completed').length}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Earnings</p>
                <p className="text-2xl font-bold text-green-600">
                  K{rides.filter(r => r.status === 'completed').reduce((sum, r) => sum + parseFloat(r.price || 0), 0).toFixed(2)}
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </DriverLayout>
  );
};

export default DriverHistory;